import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './modals.module.css';

const MAX_ZOOM_AREAS = 3;

const parsePct = (str) => parseFloat(str) / 100;

const drawRoundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const resolveImageSrc = (src) => {
  if (!src) return null;
  if (typeof src === 'string') return src;
  if (typeof src === 'object' && src.default) return src.default;
  return null;
};

const loadImg = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  if (!src.startsWith('http')) {
    img.crossOrigin = 'anonymous';
  }
  img.src = src;
});

export const AddZoomAreasModal = ({
  isOpen,
  onClose,
  sourceImage,
  zoomThumbnail,
  zoomshots = [],
  imageBbox = [0, 0, 100, 100],
  initialZoomAreas = []
}) => {
  const [zoomAreas, setZoomAreas] = useState([]);
  const [zoomLabels, setZoomLabels] = useState({});
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const imageContainerRef = useRef(null);
  const leftCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);

  const maxAreas = zoomshots.length > 0 ? Math.min(MAX_ZOOM_AREAS, zoomshots.length) : MAX_ZOOM_AREAS;

  // Mirror the template's clip shape on the selection overlay so the user sees
  // exactly the shape (circle / rounded rect / rect) that will appear in the
  // generated zoomshot. Returns a CSS borderRadius value or undefined.
  const shapeRadiusFor = (slotIdx) => zoomshots[slotIdx]?.clip || undefined;

  const drawPreview = useCallback(async () => {
    const leftCanvas = leftCanvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    const templateSrc = resolveImageSrc(zoomThumbnail);

    // Load source image once — used by both canvases.
    let srcImg = null;
    if (sourceImage?.previewUrl) {
      try { srcImg = await loadImg(sourceImage.previewUrl); } catch (e) { console.error('Source image load failed:', e); }
    }

    // --- LEFT canvas: source image only (where the user picks zoom areas).
    // Drawn independently of templateSrc so the modal is usable even for themes
    // whose zoom background URL isn't registered in zoomshot_bg_urls.json.
    if (leftCanvas) {
      const ctx = leftCanvas.getContext('2d');
      if (srcImg) {
        leftCanvas.width = srcImg.naturalWidth;
        leftCanvas.height = srcImg.naturalHeight;
        ctx.drawImage(srcImg, 0, 0);
      } else {
        ctx.clearRect(0, 0, leftCanvas.width, leftCanvas.height);
      }
    }

    // --- RIGHT preview canvas: needs the template thumbnail to be meaningful.
    if (!previewCanvas) return;
    const ctx = previewCanvas.getContext('2d');
    if (!templateSrc) {
      ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      return;
    }

    try {
      const templateImg = await loadImg(templateSrc);
      const W = templateImg.naturalWidth;
      const H = templateImg.naturalHeight;
      previewCanvas.width = W;
      previewCanvas.height = H;
      ctx.drawImage(templateImg, 0, 0);

      if (!srcImg) return;

      const bboxX = parsePct(imageBbox[0]) * W;
      const bboxY = parsePct(imageBbox[1]) * H;
      const bboxW = parsePct(imageBbox[2]) * W;
      const bboxH = parsePct(imageBbox[3]) * H;

      // Contain fit: scale to fit within bbox, preserve aspect ratio, center
      const imgAspect = srcImg.naturalWidth / srcImg.naturalHeight;
      const bboxAspect = bboxW / bboxH;
      let drawW, drawH;
      if (imgAspect > bboxAspect) {
        drawW = bboxW;
        drawH = bboxW / imgAspect;
      } else {
        drawH = bboxH;
        drawW = bboxH * imgAspect;
      }
      const drawX = bboxX + (bboxW - drawW) / 2;
      const drawY = bboxY + (bboxH - drawH) / 2;
      ctx.drawImage(srcImg, drawX, drawY, drawW, drawH);

      if (zoomAreas.length > 0 && zoomshots.length > 0) {
        zoomAreas.forEach((area, i) => {
          const zsCfg = zoomshots[i];
          if (!zsCfg) return;

          const [posLeft, posTop] = zsCfg.position;
          const destX = parsePct(posLeft) * W;
          const destY = parsePct(posTop) * H;
          const destW = parsePct(zsCfg.width) * W;
          const destH = parsePct(zsCfg.height) * H;
          const clipR = zsCfg.clip ? parsePct(zsCfg.clip) * W : 0;

          const cropX = (area.x / 100) * srcImg.naturalWidth;
          const cropY = (area.y / 100) * srcImg.naturalHeight;
          const cropW = (area.w / 100) * srcImg.naturalWidth;
          const cropH = (area.h / 100) * srcImg.naturalHeight;

          ctx.save();
          if (zsCfg.clip && parsePct(zsCfg.clip) >= 0.5) {
            ctx.beginPath();
            ctx.ellipse(destX + destW / 2, destY + destH / 2, destW / 2, destH / 2, 0, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
          } else if (clipR > 0) {
            drawRoundedRect(ctx, destX, destY, destW, destH, clipR);
            ctx.clip();
          }
          ctx.drawImage(srcImg, cropX, cropY, cropW, cropH, destX, destY, destW, destH);
          ctx.restore();
        });
      }
    } catch (err) {
      console.error('Zoom preview draw error:', err);
    }
  }, [zoomThumbnail, sourceImage, zoomAreas, zoomshots, imageBbox]);

  useEffect(() => {
    if (isOpen) drawPreview();
  }, [isOpen, drawPreview]);

  // Seed state from previously-saved zoom areas when modal opens, so user
  // can review/edit instead of starting over.
  useEffect(() => {
    if (isOpen) {
      const seeded = (initialZoomAreas || []).map((a, i) => ({
        index: i,
        x: a.x ?? 0,
        y: a.y ?? 0,
        w: a.w ?? 10,
        h: a.h ?? 10,
      }));
      const labelMap = {};
      (initialZoomAreas || []).forEach((a, i) => {
        if (a.label) labelMap[i] = a.label;
      });
      setZoomAreas(seeded);
      setZoomLabels(labelMap);
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentRect(null);
    }
  }, [isOpen, initialZoomAreas]);

  // Force a square area centered on the drag midpoint. Side length is the
  // larger of |dx|/|dy| so the square encloses the drag. Clamped to container.
  const squareFromDrag = (startX, startY, endX, endY) => {
    const cx = (startX + endX) / 2;
    const cy = (startY + endY) / 2;
    const side = Math.max(Math.abs(endX - startX), Math.abs(endY - startY));
    const half = Math.min(side / 2, cx, cy, 100 - cx, 100 - cy);
    return { x: cx - half, y: cy - half, w: half * 2, h: half * 2 };
  };

  const handleMouseDown = (e) => {
    if (zoomAreas.length >= maxAreas || !imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setIsDrawing(true);
    setDrawStart({ x, y });
    setCurrentRect({ x, y, w: 0, h: 0 });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !drawStart || !imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const ex = ((e.clientX - rect.left) / rect.width) * 100;
    const ey = ((e.clientY - rect.top) / rect.height) * 100;
    setCurrentRect(squareFromDrag(drawStart.x, drawStart.y, ex, ey));
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentRect || !drawStart) {
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentRect(null);
      return;
    }
    if (currentRect.w > 5 && currentRect.h > 5) {
      // Drag: use the centered square derived from the drag bounds
      setZoomAreas(prev => [...prev, { index: prev.length, ...currentRect }]);
    } else {
      // Click: 10%×10% square centered on click point, clamped to container
      const cx = Math.min(Math.max(drawStart.x, 5), 95);
      const cy = Math.min(Math.max(drawStart.y, 5), 95);
      setZoomAreas(prev => [...prev, { index: prev.length, x: cx - 5, y: cy - 5, w: 10, h: 10 }]);
    }
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
  };

  const removeZoomArea = (index, e) => {
    e.stopPropagation();
    setZoomAreas(prev => prev.filter(z => z.index !== index).map((z, i) => ({ ...z, index: i })));
    setZoomLabels(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = parseInt(k);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
      });
      return next;
    });
  };

  if (!isOpen) return null;

  const templateSrc = resolveImageSrc(zoomThumbnail);

  return ReactDOM.createPortal(
    <div className={styles.removeBgModalOverlay} onClick={() => onClose()}>
      <div className={styles.addZoomAreasModal} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.addZoomModalClose} onClick={() => onClose()} aria-label="Close">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 9.4L22.6 8L16 14.6L9.4 8L8 9.4L14.6 16L8 22.6L9.4 24L16 17.4L22.6 24L24 22.6L17.4 16L24 9.4Z" fill="#505050"/>
          </svg>
        </button>

        <div className={styles.addZoomAreasModalContent}>
          <div className={styles.addZoomAreasSection}>
            <h2 className={styles.addZoomAreasSectionTitle}>Add Zoom Areas</h2>
            <div className={styles.addZoomAreasInstructions}>
              Click (or click and drag) on the image to select up to {maxAreas} area{maxAreas !== 1 ? 's' : ''} to zoom into.
            </div>
            <div
              ref={imageContainerRef}
              className={styles.addZoomAreasImageContainer}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <canvas ref={leftCanvasRef} className={styles.addZoomAreasImage} />
              {zoomAreas.map((area) => (
                <div
                  key={area.index}
                  className={styles.zoomAreaBox}
                  style={{
                    left: `${area.x}%`,
                    top: `${area.y}%`,
                    width: `${area.w}%`,
                    height: `${area.h}%`,
                    borderRadius: shapeRadiusFor(area.index),
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <span className={styles.zoomAreaLabel}>Zoom Area {area.index + 1}</span>
                  <button type="button" className={styles.zoomAreaRemove} onClick={(e) => removeZoomArea(area.index, e)} aria-label="Remove">×</button>
                  <div className={styles.zoomAreaResizeHandle} />
                </div>
              ))}
              {currentRect && (
                <div
                  className={styles.zoomAreaBoxDrawing}
                  style={{
                    left: `${currentRect.x}%`,
                    top: `${currentRect.y}%`,
                    width: `${currentRect.w}%`,
                    height: `${currentRect.h}%`,
                    borderRadius: shapeRadiusFor(zoomAreas.length),
                  }}
                />
              )}
            </div>
            {zoomAreas.length > 0 && (
              <div className={styles.zoomLabelsSection}>
                {zoomAreas.map((area) => (
                  <div key={area.index} className={styles.zoomLabelRow}>
                    <span className={styles.zoomLabelNumber}>Zoom {area.index + 1}:</span>
                    <input
                      type="text"
                      className={styles.zoomLabelInput}
                      placeholder="e.g. HD Display"
                      maxLength={30}
                      value={zoomLabels[area.index] || ''}
                      onChange={(e) => setZoomLabels(prev => ({ ...prev, [area.index]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.addZoomAreasPreviewSection}>
            <h2 className={styles.addZoomAreasSectionTitle}>Preview</h2>
            <div className={styles.addZoomAreasPreviewBox}>
              {templateSrc ? (
                <canvas ref={previewCanvasRef} className={styles.addZoomAreasPreviewCanvas} />
              ) : (
                <div className={styles.addZoomModalPlaceholder}>
                  <span className={styles.addZoomModalPlaceholderText}>Zoom preview placeholder</span>
                </div>
              )}
            </div>
            <div className={styles.addZoomAreasDoneWrapper}>
              <button type="button" className={styles.addZoomModalPrimaryButton} onClick={() => onClose(zoomAreas.map(a => ({ ...a, label: zoomLabels[a.index] || '' })))}>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
